import { Link } from "react-router-dom";
import { Trash, Plus, Minus, ShoppingCartSimple, Lock, ArrowUpRight } from "@phosphor-icons/react";
import PageShell from "@/components/site/PageShell";
import { useCart } from "@/context/CartContext";
import { formatUSD } from "@/data/products";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, count, clear } = useCart();

  return (
    <PageShell>
      <section className="bg-background py-12 md:py-20" data-testid="cart-page">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="flex items-baseline justify-between mb-10">
            <div>
              <span className="mono-label text-primary">/ Cart</span>
              <h1 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.9]">
                Your order<span className="text-primary">.</span>
              </h1>
            </div>
            {count > 0 && (
              <button
                onClick={clear}
                className="mono-label text-muted-foreground hover:text-primary transition-colors"
                data-testid="cart-clear"
              >
                Clear cart
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div
              className="border border-white/10 p-12 md:p-20 text-center"
              data-testid="cart-empty"
            >
              <ShoppingCartSimple
                size={48}
                weight="light"
                className="mx-auto text-muted-foreground mb-6"
              />
              <h2 className="font-display font-bold uppercase tracking-tight text-2xl md:text-3xl mb-3">
                Cart is empty
              </h2>
              <p className="text-foreground/60 mb-8">
                Browse the full inventory to start an order.
              </p>
              <Link
                to="/inventory"
                className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-6 py-4 mono-label hover:bg-foreground hover:text-background transition-colors"
                data-testid="cart-empty-cta"
              >
                Browse Inventory
                <ArrowUpRight size={16} weight="bold" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8 border border-white/10">
                <div className="hidden md:grid grid-cols-12 border-b border-white/10 px-5 py-3 mono-label text-muted-foreground">
                  <div className="col-span-6">Item</div>
                  <div className="col-span-2 text-right">Unit price</div>
                  <div className="col-span-2 text-center">Qty</div>
                  <div className="col-span-2 text-right">Line total</div>
                </div>
                {items.map((i) => (
                  <div
                    key={i.sku}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-4 border-b border-white/10 items-center"
                    data-testid={`cart-row-${i.sku}`}
                  >
                    <div className="md:col-span-6">
                      <p className="font-display font-medium leading-tight">{i.name}</p>
                      <p className="mono-label text-muted-foreground mt-1">
                        {i.sku} · {i.unit}
                      </p>
                    </div>
                    <div className="md:col-span-2 text-left md:text-right font-mono-tech">
                      {formatUSD(i.price)}
                    </div>
                    <div className="md:col-span-2 flex items-center justify-start md:justify-center">
                      <div className="inline-flex items-center border border-white/15">
                        <button
                          onClick={() => updateQty(i.sku, i.qty - 1)}
                          className="p-2 hover:bg-secondary transition-colors"
                          aria-label="Decrease"
                          data-testid={`cart-dec-${i.sku}`}
                        >
                          <Minus size={14} weight="bold" />
                        </button>
                        <span
                          className="px-4 font-mono-tech text-sm"
                          data-testid={`cart-qty-${i.sku}`}
                        >
                          {i.qty}
                        </span>
                        <button
                          onClick={() => updateQty(i.sku, i.qty + 1)}
                          className="p-2 hover:bg-secondary transition-colors"
                          aria-label="Increase"
                          data-testid={`cart-inc-${i.sku}`}
                        >
                          <Plus size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                    <div className="md:col-span-2 flex items-center justify-between md:justify-end gap-4">
                      <span
                        className="font-mono-tech text-lg"
                        data-testid={`cart-line-${i.sku}`}
                      >
                        {formatUSD(i.price * i.qty)}
                      </span>
                      <button
                        onClick={() => removeItem(i.sku)}
                        className="text-muted-foreground hover:text-primary transition-colors"
                        aria-label="Remove"
                        data-testid={`cart-remove-${i.sku}`}
                      >
                        <Trash size={18} weight="light" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <aside
                className="lg:col-span-4 border border-white/10 p-6 md:p-8 h-fit lg:sticky lg:top-24"
                data-testid="cart-summary"
              >
                <p className="mono-label text-primary mb-4">/ Order summary</p>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="mono-label text-muted-foreground">Items</span>
                  <span className="font-mono-tech">{count}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="mono-label text-muted-foreground">Subtotal</span>
                  <span className="font-mono-tech text-lg">{formatUSD(subtotal)}</span>
                </div>
                <p className="mono-label text-muted-foreground mt-4 mb-6 leading-relaxed">
                  Shipping, taxes and freight are calculated on your invoice after the
                  order is placed. Our team will confirm within 1 business day.
                </p>
                <Link
                  to="/checkout"
                  className="w-full inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-6 py-4 mono-label hover:bg-foreground hover:text-background transition-colors"
                  data-testid="cart-checkout-cta"
                >
                  <Lock size={16} weight="bold" />
                  Secure Checkout
                  <ArrowUpRight size={16} weight="bold" />
                </Link>
                <div className="mt-4 flex items-center gap-2 justify-center text-muted-foreground">
                  <Lock size={12} weight="fill" className="text-primary" />
                  <span className="mono-label text-[0.65rem]">
                    HTTPS · SSL secured checkout
                  </span>
                </div>
                <Link
                  to="/inventory"
                  className="block text-center mt-4 mono-label text-muted-foreground hover:text-primary transition-colors"
                  data-testid="cart-continue-shopping"
                >
                  ← Continue shopping
                </Link>
              </aside>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
